import React, { useEffect, useState } from 'react';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { showConfirm } from '../common/AppDialog';
import { listCanvasVersions, loadCanvas, CanvasDoc, CanvasVersionInfo } from '../../services/repo';
import FlowPreview from './wizard/FlowPreview';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../utils/formatters';

interface Props {
  workflowId: string;
  workflowName: string;
  curVerNo: number;
  onClose: () => void;
  /** 고른 버전을 새 버전으로 저장하고 캔버스에 편다 (이력은 지우지 않는다) */
  onRestore: (doc: CanvasDoc, verNo: number) => Promise<void>;
}

const fmtDt = (s?: string) => {
  if (!s) return '';
  const v = String(s);
  if (/^\d{14}$/.test(v)) return `${v.slice(0, 4)}-${v.slice(4, 6)}-${v.slice(6, 8)} ${v.slice(8, 10)}:${v.slice(10, 12)}`;
  return formatDateTime(v, { seconds: false });   // 시간대를 맞춘다 — 문자열을 자르면 UTC 가 그대로 보였다
};

/**
 * 다이어그램 버전 이력 — 저장(Save)마다 쌓인 버전을 최신순으로 보여 주고,
 * 고른 버전을 미리보기로 확인한 뒤 "이 버전으로 복구" 할 수 있다.
 * 복구는 과거 버전을 덮어쓰는 게 아니라 그 내용을 **새 버전으로 저장**한다 — 이력이 항상 남는다.
 */
const VersionHistoryDialog: React.FC<Props> = ({ workflowId, workflowName, curVerNo, onClose, onRestore }) => {
  const { t } = useTranslation();
  useEscapeKey(onClose);
  const [versions, setVersions] = useState<CanvasVersionInfo[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sel, setSel] = useState<number>(0);
  const [doc, setDoc] = useState<CanvasDoc | null>(null);
  const [docErr, setDocErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listCanvasVersions(workflowId)
      .then((v) => { setVersions(v); if (v.length) setSel(v[0].verNo); })
      .catch((e) => setError((e as Error).message));
  }, [workflowId]);

  useEffect(() => {
    if (!sel) return;
    setDoc(null); setDocErr(null);
    loadCanvas(workflowId, sel)
      .then((d) => { if (d) setDoc(d); else setDocErr(t('dialog:version.notFound')); })
      .catch((e) => setDocErr((e as Error).message));
  }, [workflowId, sel, t]);

  const restore = async () => {
    if (!doc) return;
    const ok = await showConfirm(
      t('dialog:version.restoreConfirm', { sel, next: curVerNo + 1 }),
      { title: t('dialog:version.restoreTitle'), okText: t('dialog:version.restore') },
    );
    if (!ok) return;
    setBusy(true);
    try {
      await onRestore(doc, sel);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-40" onMouseDown={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-[900px] max-w-[95vw] h-[72vh] flex flex-col" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div>
            <div className="text-sm font-semibold text-gray-800">{t('dialog:version.title', { name: workflowName })}</div>
            <div className="text-[0.6875rem] text-gray-500">{t('dialog:version.subtitle')}</div>
          </div>
          <button className="px-2 py-1 text-xs rounded hover:bg-gray-100 text-gray-600" onClick={onClose} title={t('dialog:version.closeEsc')}>✕</button>
        </div>

        <div className="flex-1 min-h-0 grid grid-cols-[240px_1fr] divide-x divide-gray-200">
          {/* 버전 목록 */}
          <div className="overflow-auto py-1 bg-gray-50">
            {error ? <div className="p-3 text-xs text-red-500">{error}</div>
              : versions === null ? <div className="p-3 text-xs text-gray-500">{t('dialog:version.reading')}</div>
                : versions.length === 0 ? <div className="p-3 text-xs text-gray-500">{t('dialog:version.empty')}</div>
                  : versions.map((v) => (
                    <button key={v.verNo} type="button" onClick={() => setSel(v.verNo)}
                      className={`w-full text-left px-3 py-2 text-sm border-l-2 ${sel === v.verNo ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-transparent text-gray-700 hover:bg-gray-100'}`}>
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold">v{v.verNo}</span>
                        {v.verNo === curVerNo && <span className="text-[0.625rem] px-1 rounded bg-green-100 text-green-700">{t('dialog:version.current')}</span>}
                      </div>
                      <div className="text-[0.6875rem] text-gray-500">{fmtDt(v.regDt)}{v.regUsrid ? ` · ${v.regUsrid}` : ''}</div>
                    </button>
                  ))}
          </div>

          {/* 미리보기 */}
          <div className="overflow-auto p-3 min-h-0">
            {docErr ? <div className="text-xs text-red-500">{docErr}</div>
              : !doc ? <div className="text-xs text-gray-500">{t('dialog:version.reading')}</div>
                : (
                  <>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="text-sm font-semibold text-gray-800">v{sel}</div>
                      <div className="text-[0.6875rem] text-gray-500">{t('dialog:version.counts', { nodes: doc.nodes.length, edges: doc.edges.length })}</div>
                      <button type="button"
                        className="ml-auto px-3 py-1 text-xs rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
                        disabled={busy || sel === curVerNo}
                        title={sel === curVerNo ? t('dialog:version.alreadyCurrent') : t('dialog:version.restoreHint', { sel, next: curVerNo + 1 })}
                        onClick={restore}>
                        {busy ? t('dialog:version.restoring') : t('dialog:version.restoreThis')}
                      </button>
                    </div>
                    <FlowPreview nodes={doc.nodes} edges={doc.edges} height={360} />
                  </>
                )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VersionHistoryDialog;
